// Client-local particle VFX: instanced, pooled, decoupled from the render rate.
//
// Presentation only — NOT networked and NOT part of the deterministic sim, so
// `Math.random`/wall-clock are fine here and ghosts/replays/lag comp are
// unaffected.
//
// Design (ideas from the Vercidium particle system, reimplemented):
//   - one instanced draw call (camera-facing billboards),
//   - struct-of-arrays pool with swap-and-pop removal (no per-particle alloc),
//   - bounded batch spawn,
//   - transform computed from just position/colour/size (no per-particle matrix),
//   - per-instance data uploaded into a pre-allocated, orphaned GPU buffer so the
//     upload can't stall on the previous frame (no CPU↔GPU sync point),
//   - updated on its own cadence by the scheduler, never by the sim.

const FLOATS_PER_INSTANCE = 9; // pos.xyz + colour.rgb + size + alpha + rotation

/** Blending/fade style of a particle system. */
export type ParticleBlend = "additive" | "alpha";

/** Sprite shape: a hard low-poly facet blob, or a soft round puff. */
export type ParticleSprite = "facet" | "soft" | "ring";

/**
 * Procedurally build a low-poly facet sprite: an irregular polygon split into
 * flat-shaded facets (brightness varies per facet) with a hot core toward the
 * centre. RGB is a brightness mask (hue comes from the particle colour); alpha
 * is the polygon coverage. Hard edges are intentional (low-poly look).
 */
function makeFacetSprite(size: number): { w: number; h: number; data: Uint8Array } {
  const n = 9;
  const cx = size / 2;
  const cy = size / 2;
  const rMax = size / 2 - 1;
  const verts: [number, number][] = [];
  const facet: number[] = [];
  for (let i = 0; i < n; i++) {
    const a = (i / n) * Math.PI * 2 + Math.random() * 0.25;
    const r = rMax * (0.68 + Math.random() * 0.32);
    verts.push([cx + Math.cos(a) * r, cy + Math.sin(a) * r]);
    facet.push(0.72 + Math.random() * 0.28);
  }
  const sign = (px: number, py: number, ax: number, ay: number, bx: number, by: number) =>
    (px - bx) * (ay - by) - (ax - bx) * (py - by);
  const data = new Uint8Array(size * size * 4);
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      const px = x + 0.5;
      const py = y + 0.5;
      let hit = -1;
      for (let i = 0; i < n; i++) {
        const a = verts[i];
        const b = verts[(i + 1) % n];
        const d1 = sign(px, py, cx, cy, a[0], a[1]);
        const d2 = sign(px, py, a[0], a[1], b[0], b[1]);
        const d3 = sign(px, py, b[0], b[1], cx, cy);
        const neg = d1 < 0 || d2 < 0 || d3 < 0;
        const pos = d1 > 0 || d2 > 0 || d3 > 0;
        if (!(neg && pos)) {
          hit = i;
          break;
        }
      }
      const o = (y * size + x) * 4;
      if (hit >= 0) {
        const dxr = (px - cx) / rMax;
        const dyr = (py - cy) / rMax;
        const d = Math.sqrt(dxr * dxr + dyr * dyr);
        const core = Math.max(0, 1 - d * 1.15);
        const bright = Math.min(1, facet[hit] * (0.82 + 0.4 * core));
        const v = Math.round(bright * 255);
        data[o] = v;
        data[o + 1] = v;
        data[o + 2] = v;
        data[o + 3] = 255;
      }
    }
  }
  return { w: size, h: size, data };
}

/**
 * Procedural ring / disc sprite (Q3 `railDisc`-ish): a bright soft ring with a
 * faint centre glow. RGB is a white mask; alpha is coverage.
 */
function makeRingSprite(size: number): { w: number; h: number; data: Uint8Array } {
  const cx = (size - 1) / 2;
  const cy = (size - 1) / 2;
  const maxR = size / 2 - 1;
  const data = new Uint8Array(size * size * 4);
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      const dx = (x - cx) / maxR;
      const dy = (y - cy) / maxR;
      const d = Math.sqrt(dx * dx + dy * dy);
      const ring = Math.max(0, 1 - Math.abs(d - 0.72) / 0.22);
      const core = Math.max(0, 1 - d / 0.34);
      const a = Math.min(1, ring * ring + core * 0.16);
      const o = (y * size + x) * 4;
      data[o] = 255;
      data[o + 1] = 255;
      data[o + 2] = 255;
      data[o + 3] = Math.round(a * 255);
    }
  }
  return { w: size, h: size, data };
}

export interface EmitOptions {
  /** How many particles to spawn (clamped to the free pool). */
  count: number;
  /** World-space spawn point (all particles start here). */
  pos: readonly [number, number, number];
  /** Base velocity added to the random spread (units/s). */
  vel?: readonly [number, number, number];
  /** Random velocity spread magnitude (units/s). */
  spread?: number;
  /** Base colour (linear-ish 0..1). */
  color?: readonly [number, number, number];
  /** Per-particle colour jitter, 0..1 (fraction of the channel value). */
  colorJitter?: number;
  /** Base billboard size (world units). */
  size?: number;
  /** Per-particle size jitter, 0..1. */
  sizeJitter?: number;
  /** Lifetime (ms). */
  lifeMs?: number;
  /** Per-particle lifetime jitter, 0..1. */
  lifeJitter?: number;
  /** Downward acceleration (units/s²). */
  gravity?: number;
  /** Velocity damping per second (0..1). */
  drag?: number;
  /** Alpha multiplier for every particle in this emit (0..1, default 1). */
  alpha?: number;
}

const DEFAULT_COLOR: readonly [number, number, number] = [1, 0.8, 0.4];

export class ParticleSystem {
  private gl: WebGL2RenderingContext;
  private readonly max: number;
  private readonly blend: ParticleBlend;
  private readonly sprite: ParticleSprite;
  private spriteTex: WebGLTexture;
  private uSprite: WebGLUniformLocation | null = null;
  /** Emit-count multiplier (Debug tuning). */
  density = 1;
  /** Instance-alpha multiplier (Debug tuning; lowers smoke opacity). */
  opacity = 1;
  /** Billboard-size multiplier (Debug tuning). */
  sizeScale = 1;
  /**
   * Per-emit multipliers, set by a caller for one `emit` (per-weapon VFX) and
   * then reset. They bake into the spawned particles; the draw-time `density`
   * (spawn), `sizeScale` and `opacity` above remain the global Video knobs.
   */
  emitDensity = 1;
  emitSize = 1;
  emitOpacity = 1;
  /**
   * Optional world trace for collision. Given the segment, returns
   * `[fraction, nx, ny, nz, startSolid]`, or null when there's no world.
   */
  collide:
    | ((x0: number, y0: number, z0: number, x1: number, y1: number, z1: number) => Float32Array | null)
    | null = null;
  /** Collision box radius (world units); <= 0 disables it. */
  collideRadius = 1.5;
  /** Max particles traced per step (cost cap). */
  collideMax = 256;

  // Pool (struct-of-arrays).
  private px: Float32Array;
  private py: Float32Array;
  private pz: Float32Array;
  private vx: Float32Array;
  private vy: Float32Array;
  private vz: Float32Array;
  private life: Float32Array; // remaining ms
  private maxLife: Float32Array;
  private size: Float32Array;
  private cr: Float32Array;
  private cg: Float32Array;
  private cb: Float32Array;
  private gravity: Float32Array;
  private drag: Float32Array;
  private rot: Float32Array;
  /** Per-particle alpha multiplier (per-emit, e.g. per-weapon orb opacity). */
  private pa: Float32Array;
  private active = 0;

  // GPU.
  private program: WebGLProgram;
  private vao: WebGLVertexArrayObject;
  private cornerVbo: WebGLBuffer;
  private instanceVbo: WebGLBuffer;
  private readonly capacityBytes: number;
  private inst: Float32Array; // interleaved staging buffer
  private uProjView: WebGLUniformLocation | null;
  private uEye: WebGLUniformLocation | null;

  /** Last update wall-clock (ms) for the decoupled fixed-step integration. */
  private lastUpdate = 0;

  constructor(
    gl: WebGL2RenderingContext,
    max = 4096,
    blend: ParticleBlend = "additive",
    sprite: ParticleSprite = "facet",
  ) {
    this.gl = gl;
    this.max = max;
    this.blend = blend;
    this.sprite = sprite;
    const f = () => new Float32Array(max);
    this.px = f(); this.py = f(); this.pz = f();
    this.vx = f(); this.vy = f(); this.vz = f();
    this.life = f(); this.maxLife = f(); this.size = f();
    this.cr = f(); this.cg = f(); this.cb = f();
    this.gravity = f(); this.drag = f(); this.rot = f();
    this.pa = f();
    this.inst = new Float32Array(max * FLOATS_PER_INSTANCE);
    this.capacityBytes = this.inst.byteLength;

    this.program = this.link();
    this.uProjView = gl.getUniformLocation(this.program, "u_proj_view");
    this.uEye = gl.getUniformLocation(this.program, "u_eye");
    this.uSprite = gl.getUniformLocation(this.program, "u_sprite");
    const spriteMode = gl.getUniformLocation(this.program, "u_sprite_mode");
    gl.useProgram(this.program);
    if (this.uSprite) gl.uniform1i(this.uSprite, 3);
    if (spriteMode) gl.uniform1f(spriteMode, this.sprite === "soft" ? 0 : 1);

    // Sprite texture (soft mode ignores it).
    this.spriteTex = gl.createTexture()!;
    gl.activeTexture(gl.TEXTURE3);
    gl.bindTexture(gl.TEXTURE_2D, this.spriteTex);
    const sp = this.sprite === "ring" ? makeRingSprite(64) : makeFacetSprite(64);
    gl.pixelStorei(gl.UNPACK_ALIGNMENT, 1);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, sp.w, sp.h, 0, gl.RGBA, gl.UNSIGNED_BYTE, sp.data);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);

    // Camera-facing quad (two triangles), corners in [-0.5, 0.5].
    this.cornerVbo = gl.createBuffer()!;
    gl.bindBuffer(gl.ARRAY_BUFFER, this.cornerVbo);
    gl.bufferData(
      gl.ARRAY_BUFFER,
      new Float32Array([-0.5, -0.5, 0.5, -0.5, 0.5, 0.5, -0.5, -0.5, 0.5, 0.5, -0.5, 0.5]),
      gl.STATIC_DRAW,
    );

    this.instanceVbo = gl.createBuffer()!;
    gl.bindBuffer(gl.ARRAY_BUFFER, this.instanceVbo);
    gl.bufferData(gl.ARRAY_BUFFER, this.capacityBytes, gl.DYNAMIC_DRAW);

    this.vao = gl.createVertexArray()!;
    gl.bindVertexArray(this.vao);
    gl.bindBuffer(gl.ARRAY_BUFFER, this.cornerVbo);
    gl.enableVertexAttribArray(0);
    gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0);
    gl.bindBuffer(gl.ARRAY_BUFFER, this.instanceVbo);
    const stride = FLOATS_PER_INSTANCE * 4;
    gl.enableVertexAttribArray(1);
    gl.vertexAttribPointer(1, 3, gl.FLOAT, false, stride, 0);
    gl.vertexAttribDivisor(1, 1);
    gl.enableVertexAttribArray(2);
    gl.vertexAttribPointer(2, 3, gl.FLOAT, false, stride, 12);
    gl.vertexAttribDivisor(2, 1);
    gl.enableVertexAttribArray(3);
    gl.vertexAttribPointer(3, 1, gl.FLOAT, false, stride, 24);
    gl.vertexAttribDivisor(3, 1);
    gl.enableVertexAttribArray(4);
    gl.vertexAttribPointer(4, 1, gl.FLOAT, false, stride, 28);
    gl.vertexAttribDivisor(4, 1);
    gl.enableVertexAttribArray(5);
    gl.vertexAttribPointer(5, 1, gl.FLOAT, false, stride, 32);
    gl.vertexAttribDivisor(5, 1);
    gl.bindVertexArray(null);
  }

  private link(): WebGLProgram {
    const gl = this.gl;
    const vs = `#version 300 es
      layout(location=0) in vec2 a_corner;
      layout(location=1) in vec3 a_pos;
      layout(location=2) in vec3 a_color;
      layout(location=3) in float a_size;
      layout(location=4) in float a_alpha;
      layout(location=5) in float a_rot;
      uniform mat4 u_proj_view;
      uniform vec3 u_eye;
      out vec3 v_color;
      out vec2 v_uv;
      out float v_alpha;
      void main() {
        // Spherical billboard: face the camera using only the eye position.
        vec3 toEye = normalize(u_eye - a_pos);
        vec3 up0 = abs(toEye.y) > 0.99 ? vec3(0.0, 0.0, 1.0) : vec3(0.0, 1.0, 0.0);
        vec3 right = normalize(cross(up0, toEye));
        vec3 up = cross(toEye, right);
        float c = cos(a_rot);
        float s = sin(a_rot);
        vec2 corner = vec2(a_corner.x * c - a_corner.y * s, a_corner.x * s + a_corner.y * c);
        vec3 world = a_pos + (right * corner.x + up * corner.y) * a_size;
        gl_Position = u_proj_view * vec4(world, 1.0);
        v_color = a_color;
        v_uv = corner + 0.5; // [-0.5,0.5] -> [0,1]
        v_alpha = a_alpha;
      }`;
    const fs = `#version 300 es
      precision mediump float;
      in vec3 v_color;
      in vec2 v_uv;
      in float v_alpha;
      uniform sampler2D u_sprite;
      uniform float u_sprite_mode; // 1 = low-poly facet sprite, 0 = soft round
      out vec4 outColor;
      void main() {
        float cov;
        vec3 mask;
        if (u_sprite_mode > 0.5) {
          vec4 t = texture(u_sprite, v_uv);
          cov = t.a;
          mask = t.rgb;
        } else {
          vec2 p = v_uv - 0.5;
          float d = dot(p, p);              // 0 at centre, 0.25 at a corner
          if (d > 0.25) discard;
          cov = smoothstep(0.25, 0.0, d);   // soft round sprite
          mask = vec3(1.0);
        }
        outColor = vec4(v_color * mask, cov * v_alpha);
      }`;
    const compile = (type: number, src: string) => {
      const s = gl.createShader(type)!;
      gl.shaderSource(s, src);
      gl.compileShader(s);
      if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) {
        throw new Error("particle shader: " + gl.getShaderInfoLog(s));
      }
      return s;
    };
    const p = gl.createProgram()!;
    gl.attachShader(p, compile(gl.VERTEX_SHADER, vs));
    gl.attachShader(p, compile(gl.FRAGMENT_SHADER, fs));
    gl.linkProgram(p);
    if (!gl.getProgramParameter(p, gl.LINK_STATUS)) {
      throw new Error("particle link: " + gl.getProgramInfoLog(p));
    }
    return p;
  }

  /** Active particle count (for the profiler / debug). */
  count(): number {
    return this.active;
  }

  /** Drop all live particles (e.g. on map load). */
  clear(): void {
    this.active = 0;
  }

  /** Spawn a batch of particles. Silently clamps to the free pool. */
  emit(o: EmitOptions): void {
    const n = Math.min(
      Math.round(o.count * this.density * this.emitDensity),
      this.max - this.active,
    );
    if (n <= 0) return;
    const [bx, by, bz] = o.pos;
    const [vvx, vvy, vvz] = o.vel ?? [0, 0, 0];
    const col = o.color ?? DEFAULT_COLOR;
    const spread = o.spread ?? 0;
    const size = o.size ?? 3;
    const sizeJ = o.sizeJitter ?? 0;
    const lifeMs = o.lifeMs ?? 600;
    const lifeJ = o.lifeJitter ?? 0;
    const colJ = o.colorJitter ?? 0;
    const grav = o.gravity ?? 800;
    const drag = o.drag ?? 0.6;
    const rnd = (s: number) => (Math.random() * 2 - 1) * s;

    for (let k = 0; k < n; k++) {
      const i = this.active++;
      this.px[i] = bx; this.py[i] = by; this.pz[i] = bz;
      this.vx[i] = vvx + rnd(spread);
      this.vy[i] = vvy + rnd(spread);
      this.vz[i] = vvz + rnd(spread);
      this.cr[i] = col[0] * (1 + rnd(colJ));
      this.cg[i] = col[1] * (1 + rnd(colJ));
      this.cb[i] = col[2] * (1 + rnd(colJ));
      this.size[i] = Math.max(0.1, size * (1 + rnd(sizeJ)) * this.emitSize);
      const l = Math.max(1, lifeMs * (1 + rnd(lifeJ)));
      this.life[i] = l;
      this.maxLife[i] = l;
      this.gravity[i] = grav;
      this.drag[i] = drag;
      this.rot[i] = Math.random() * 6.2831853;
      this.pa[i] = (o.alpha ?? 1) * this.emitOpacity;
    }
  }

  /**
   * Decoupled fixed step: integrate by `now`-derived dt (clamped). Called from
   * the scheduler, independent of the render rate.
   */
  update(now: number): void {
    if (this.lastUpdate === 0) this.lastUpdate = now;
    const dt = Math.min(0.1, Math.max(0, (now - this.lastUpdate) / 1000));
    this.lastUpdate = now;
    if (dt <= 0 || this.active === 0) return;
    const dtMs = dt * 1000;
    let traced = 0;
    for (let i = 0; i < this.active; ) {
      const l = this.life[i] - dtMs;
      if (l <= 0) {
        // Swap-and-pop.
        const last = --this.active;
        this.px[i] = this.px[last]; this.py[i] = this.py[last]; this.pz[i] = this.pz[last];
        this.vx[i] = this.vx[last]; this.vy[i] = this.vy[last]; this.vz[i] = this.vz[last];
        this.life[i] = this.life[last]; this.maxLife[i] = this.maxLife[last];
        this.size[i] = this.size[last];
        this.cr[i] = this.cr[last]; this.cg[i] = this.cg[last]; this.cb[i] = this.cb[last];
        this.gravity[i] = this.gravity[last]; this.drag[i] = this.drag[last];
        this.rot[i] = this.rot[last];
        this.pa[i] = this.pa[last];
        continue;
      }
      this.life[i] = l;
      const damp = Math.max(0, 1 - this.drag[i] * dt);
      // Gravity is along world −Z (the engine is Z-up); `floats` are x,y,z.
      this.vz[i] -= this.gravity[i] * dt;
      this.vx[i] *= damp; this.vy[i] *= damp; this.vz[i] *= damp;
      const ox = this.px[i];
      const oy = this.py[i];
      const oz = this.pz[i];
      let nx = ox + this.vx[i] * dt;
      let ny = oy + this.vy[i] * dt;
      let nz = oz + this.vz[i] * dt;
      // World collision (capped per step). Skip particles spawned inside solid
      // (e.g. sparks on a surface) so they don't stick.
      if (this.collide && this.collideRadius > 0 && traced < this.collideMax) {
        traced++;
        const tr = this.collide(ox, oy, oz, nx, ny, nz);
        if (tr && tr[4] < 0.5 && tr[0] < 1) {
          const f = tr[0];
          nx = ox + (nx - ox) * f;
          ny = oy + (ny - oy) * f;
          nz = oz + (nz - oz) * f;
          const ex = tr[1];
          const ey = tr[2];
          const ez = tr[3];
          const vn = this.vx[i] * ex + this.vy[i] * ey + this.vz[i] * ez;
          const rest = 1.4; // (1 + restitution); bounces with ~0.4 energy
          this.vx[i] -= rest * vn * ex;
          this.vy[i] -= rest * vn * ey;
          this.vz[i] -= rest * vn * ez;
          this.vx[i] *= 0.6; this.vy[i] *= 0.6; this.vz[i] *= 0.6;
        }
      }
      this.px[i] = nx;
      this.py[i] = ny;
      this.pz[i] = nz;
      i++;
    }
  }

  /** Upload + draw. Assumes the world has filled the depth buffer. */
  draw(projView: Float32Array, eye: readonly [number, number, number]): void {
    const gl = this.gl;
    if (this.active === 0) return;
    const inst = this.inst;
    const additive = this.blend === "additive";
    for (let i = 0, o = 0; i < this.active; i++, o += FLOATS_PER_INSTANCE) {
      const t = this.life[i] / this.maxLife[i]; // 1 at spawn → 0 at death
      // additive: full brightness then a quick fade (a flash).
      // alpha: a soft puff that fades in/out and grows over its whole life.
      const a = additive ? (t <= 0.25 ? t / 0.25 : 1) : t * (1 - t) * 4;
      const sm = additive ? 1 : 1 + (1 - t) * 2.5;
      let r = this.cr[i];
      let g = this.cg[i];
      let b = this.cb[i];
      if (additive) {
        // Fire ramp: a hot white core at birth easing to the base (orange) hue.
        const hot = Math.min(1, Math.max(0, t * 1.5 - 0.25));
        const k = hot * hot;
        r += (1.0 - r) * k;
        g += (0.95 - g) * k;
        b += (0.8 - b) * k;
      }
      inst[o] = this.px[i];
      inst[o + 1] = this.py[i];
      inst[o + 2] = this.pz[i];
      inst[o + 3] = r;
      inst[o + 4] = g;
      inst[o + 5] = b;
      inst[o + 6] = this.size[i] * sm * this.sizeScale;
      inst[o + 7] = a * this.opacity * this.pa[i];
      inst[o + 8] = this.rot[i];
    }
    // Orphan the buffer, then upload only what we use: a fresh allocation can't
    // stall on the previous frame's draw (no CPU↔GPU sync point).
    gl.bindBuffer(gl.ARRAY_BUFFER, this.instanceVbo);
    gl.bufferData(gl.ARRAY_BUFFER, this.capacityBytes, gl.DYNAMIC_DRAW);
    gl.bufferSubData(gl.ARRAY_BUFFER, 0, inst.subarray(0, this.active * FLOATS_PER_INSTANCE));

    gl.useProgram(this.program);
    gl.uniformMatrix4fv(this.uProjView, false, projView);
    gl.uniform3f(this.uEye, eye[0], eye[1], eye[2]);
    gl.enable(gl.DEPTH_TEST);
    gl.depthMask(false);
    gl.enable(gl.BLEND);
    if (this.blend === "additive") gl.blendFunc(gl.SRC_ALPHA, gl.ONE); // glow
    else gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA); // smoke/alpha
    gl.bindVertexArray(this.vao);
    gl.drawArraysInstanced(gl.TRIANGLES, 0, 6, this.active);
    gl.bindVertexArray(null);
    gl.depthMask(true);
    gl.disable(gl.BLEND);
  }

  // ---- Convenience emitters -------------------------------------------------

  /** Layered low-poly fireball + sparks for a rocket/explosion. */
  explosion(
    pos: readonly [number, number, number],
    scale = 1,
    fire: readonly [number, number, number] = [1.0, 0.5, 0.12],
    sparkColor: readonly [number, number, number] = [1.0, 0.9, 0.5],
    alpha = 1,
  ): void {
    // Dense fireball core: large, slow, faceted blobs.
    this.emit({
      count: Math.round(13 * scale), pos, spread: 90 * scale, size: 9.5 * scale,
      sizeJitter: 0.3, color: fire, colorJitter: 0.15,
      lifeMs: 420, lifeJitter: 0.4, gravity: 60, drag: 2.2, alpha,
    });
    // Faster tongues of fire.
    this.emit({
      count: Math.round(22 * scale), pos, spread: 230 * scale, size: 5 * scale,
      color: fire, colorJitter: 0.2, lifeMs: 540, lifeJitter: 0.5,
      gravity: 220, drag: 1.4, alpha,
    });
    // Bright sparks.
    this.emit({
      count: Math.round(16 * scale), pos, spread: 380 * scale, size: 2.2 * scale,
      color: sparkColor, colorJitter: 0.1, lifeMs: 380, lifeJitter: 0.6,
      gravity: 500, drag: 0.8, alpha,
    });
  }

  /** Small bright impact sparks (hitscan hit / bullet puff). */
  spark(
    pos: readonly [number, number, number],
    dir?: readonly [number, number, number],
    color: readonly [number, number, number] = [1.0, 0.85, 0.4],
    alpha = 1,
  ): void {
    const vel = dir ? [dir[0] * 120, dir[1] * 120, dir[2] * 120] as const : undefined;
    this.emit({
      count: 8, pos, vel, spread: 60, size: 1.6,
      color, colorJitter: 0.2, lifeMs: 300, lifeJitter: 0.5,
      gravity: 300, drag: 1.2, alpha,
    });
  }

  /** Muzzle flash at the given world position, biased along `dir`. */
  muzzle(
    pos: readonly [number, number, number],
    dir: readonly [number, number, number],
    color: readonly [number, number, number] = [1.0, 0.8, 0.35],
  ): void {
    this.emit({
      count: 4, pos,
      vel: [dir[0] * 60, dir[1] * 60, dir[2] * 60],
      spread: 40, size: 2.2,
      color, colorJitter: 0.15, lifeMs: 60, lifeJitter: 0.4,
      gravity: 0, drag: 2,
    });
  }

  /** A short trailing glow puff for a flying projectile. */
  trail(
    pos: readonly [number, number, number],
    color: readonly [number, number, number],
    alpha = 1,
    lifeMs = 320,
  ): void {
    this.emit({
      count: 1, pos, spread: 12, size: 2.0, color, colorJitter: 0.1,
      lifeMs, lifeJitter: 0.4, gravity: 40, drag: 1.0, alpha,
    });
  }

  /** A soft smoke/dust cloud (use on an alpha-blended system). */
  puff(
    pos: readonly [number, number, number],
    scale = 1,
    color: readonly [number, number, number] = [0.45, 0.45, 0.5],
  ): void {
    this.emit({
      count: Math.round(9 * scale), pos, spread: 55 * scale, size: 16 * scale,
      sizeJitter: 0.4, color, colorJitter: 0.25,
      lifeMs: 1000, lifeJitter: 0.5, gravity: -40, drag: 1.2,
    });
  }

  /** A thin rising smoke wisp (projectile trails, muzzle smoke). */
  wisp(
    pos: readonly [number, number, number],
    color: readonly [number, number, number] = [0.55, 0.55, 0.6],
    size = 6,
    lifeMs = 550,
  ): void {
    this.emit({
      count: 1, pos, spread: 8, size, color, colorJitter: 0.15,
      lifeMs, lifeJitter: 0.4, gravity: -25, drag: 0.8,
    });
  }
}
